import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFundFamilyCreate } from "gen/endpoints/fund-family/fund-family";
import { FundFamilyData } from "gen/model/fundFamilyData";
import { FundFamilyStateMachineFieldsEnum } from "gen/model/fundFamilyStateMachineFieldsEnum";
import { fundFamilyCreateBody } from "gen/schemas/fund-family/fund-family";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { FormDateInput } from "shared/components/input/date/DateInput";
import { FormMoneyInput } from "shared/components/input/text/MoneyInput";
import { FormTextInput } from "shared/components/input/text/TextInput";
import { useBoolean } from "usehooks-ts";

interface AddFundFamilyModalProps {
  onCloseComplete: () => void;
  onSuccess: (fundFamily: FundFamilyData) => void;
}

const AddFundFamilyModal: React.FC<AddFundFamilyModalProps> = ({
  onSuccess,
  onCloseComplete,
}) => {
  const { t } = useTranslation(["common", "fundFamily"]);

  const { value: isOpen, setFalse: close } = useBoolean(true);

  const { mutateAsync: createFundFamily } = useFundFamilyCreate();

  const form = useForm<FundFamilyData>({
    resolver: zodResolver(fundFamilyCreateBody),
    defaultValues: {
      state: FundFamilyStateMachineFieldsEnum.draft, // TODO: Pass data back from API call instead of specifying server defaults on FE
      name: "",
      target_raise: undefined,
      target_first_close: undefined,
      notes: "",
    },
  });

  const onSubmit = async (data: FundFamilyData) => {
    const responseData = await createFundFamily({ data });
    onSuccess(responseData);
    close();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      onCloseComplete={onCloseComplete}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("fundFamily:add.title")}</ModalHeader>
        <Form form={form} onSubmit={onSubmit}>
          <ModalBody>
            <FormTextInput name="name" title={t("common:name")} type="text" />
            <FormMoneyInput
              name="target_raise"
              title={t("fundFamily:target.raise")}
              decimalScale={0}
            />
            <FormDateInput
              name="target_first_close"
              title={t("fundFamily:target.closeDate")}
            />
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={close} />}
              confirm={
                <FormSubmitButton>{t("fundFamily:add.save")}</FormSubmitButton>
              }
            />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};

export default AddFundFamilyModal;
